import { filter, isEmpty } from "lodash/fp";

import { get, httpDelete, patch, post } from "@app/common/api";
import { User } from "@app/common/types";
import {
  ApiEnterpriseWorkspace,
  EnterpriseRole,
  EnterpriseWorkspace,
  EnterpriseWorkspaceInvite,
  EnterpriseWorkspaceInviteStatus,
  EnterpriseWorkspaceSettings,
} from "@app/common/workspaces/types";

export interface WorkspaceUserInviteBody {
  email: string;
  teamId?: string;
  teamRole?: EnterpriseRole;
}

export interface BulkWorkspaceInviteUpdateBody {
  status: EnterpriseWorkspaceInviteStatus;
  ids: EnterpriseWorkspaceInvite["id"][];
}

interface BulkWorkspaceInviteResult {
  failures?: { errorCode: number; id: EnterpriseWorkspaceInvite["id"] }[];
  invites: EnterpriseWorkspaceInvite[];
}

export interface EnterpriseUserBody {
  userId: string;
  role: EnterpriseRole;
}

export interface PatchEnterpriseUserBody extends Partial<EnterpriseUserBody> {
  globalSessionAccessEnabled?: boolean;
}

export const createWorkspace = async (
  name: string,
  settings?: Partial<EnterpriseWorkspaceSettings>
): Promise<ApiEnterpriseWorkspace> => {
  return await post(
    "/enterprise/workspaces",
    { name, settings },
    { withCreds: true }
  );
};

export const deleteWorkspace = async (workspaceId: string): Promise<void> => {
  return await httpDelete(`/enterprise/workspaces/${workspaceId}`, {
    withCreds: true,
  });
};

export const updateWorkspaceName = async (
  workspaceId: string,
  name: string
): Promise<void> => {
  return patch(
    `/enterprise/workspaces/${workspaceId}`,
    { name },
    { withCreds: true }
  );
};

// gets the workspace invite (if it exists) for the current user and workspace id
export const getWorkspaceUserInvites = async (
  status?: EnterpriseWorkspaceInviteStatus
): Promise<EnterpriseWorkspaceInvite[]> => {
  const invites: EnterpriseWorkspaceInvite[] = await get(
    `/users/me/workspace_invites${status ? `?status=${status}` : ""}`,
    { withCreds: true }
  );

  return filter((invite) => invite.status !== "removed", invites);
};

// gets the workspace invite (if it exists) for the current user and invite id
export const getWorkspaceUserInvite = async (
  inviteId: string
): Promise<EnterpriseWorkspaceInvite> => {
  return get(`/users/me/workspace_invites/${inviteId}`, { withCreds: true });
};

export const updateWorkspaceUserInvite = async ({
  inviteId,
  isAccepted,
}: {
  inviteId: string;
  isAccepted: boolean;
}): Promise<void> => {
  await patch(
    `/users/me/workspace_invites/${inviteId}`,
    { status: isAccepted ? "accepted" : "denied" },
    { withCreds: true }
  );
};

export const getWorkspaceInvites = async (
  workspaceId: string
): Promise<EnterpriseWorkspaceInvite[]> => {
  return await get(`/enterprise/workspaces/${workspaceId}/invites`, {
    withCreds: true,
  });
};

export const updateAllWorkspaceInvites = async (
  workspaceId: EnterpriseWorkspace["id"],
  updateBody: BulkWorkspaceInviteUpdateBody
): Promise<BulkWorkspaceInviteResult> => {
  return patch(
    `/enterprise/workspaces/${workspaceId}/bulk-invites`,
    updateBody,
    { withCreds: true }
  );
};

// Migrates the specified user's meetings to the specified workspace
export const migrateWorkspaceUserMeetings = async (
  workspaceId: string,
  userId: string
): Promise<void> => {
  return await post(
    `/enterprise/workspaces/${workspaceId}/users/${userId}/import_sessions`,
    {},
    { withCreds: true }
  );
};

export const updateWorkspaceUser = async (
  workspaceId: string,
  user: PatchEnterpriseUserBody
): Promise<void> => {
  const { userId } = user;
  return await patch(
    `/enterprise/workspaces/${workspaceId}/users/${userId}`,
    user,
    { withCreds: true }
  );
};

export const deleteWorkspaceUser = async (
  workspaceId: EnterpriseWorkspace["id"],
  userId: User["id"]
): Promise<void> => {
  return httpDelete(`/enterprise/workspaces/${workspaceId}/users/${userId}`, {
    withCreds: true,
  });
};

export const deleteWorkspaceUsers = async (
  workspaceId: string,
  userIds: string[]
): Promise<void> => {
  if (isEmpty(userIds)) return;

  const body = userIds.map((userId) => ({ userId }));
  return post(`/enterprise/workspaces/${workspaceId}/users/bulk-delete`, body, {
    withCreds: true,
  });
};
